import { PlayArrow } from "@mui/icons-material"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { JsonView } from "react-json-view-lite"
import { usePostSstv2ApiTestMutation } from "src/features/partners/partnersApi"

type ApiMethod = {
  label: string
  method: string
  endpoint: string
  params: string[]
}

const API_METHODS: ApiMethod[] = [
  // --- Lockers ---
  {
    label: "GET /mobilus/lockers/{page}/{count}",
    method: "GET",
    endpoint: "/mobilus/lockers/{page}/{count}",
    params: ["page", "count"],
  },
  {
    label: "GET /mobilus/lockers/complete/{page}/{count}",
    method: "GET",
    endpoint: "/mobilus/lockers/complete/{page}/{count}",
    params: ["page", "count"],
  },
  {
    label: "GET /mobilus/lockers/{lockerId}",
    method: "GET",
    endpoint: "/mobilus/lockers/{lockerId}",
    params: ["lockerId"],
  },
  {
    label: "PATCH /mobilus/lockers/sqs/{lockerId}/{sqsTag}",
    method: "PATCH",
    endpoint: "/mobilus/lockers/sqs/{lockerId}/{sqsTag}",
    params: ["lockerId", "sqsTag"],
  },
  {
    label: "PATCH /mobilus/lockers/reference_id/{lockerId}/{referenceId}",
    method: "PATCH",
    endpoint: "/mobilus/lockers/reference_id/{lockerId}/{referenceId}",
    params: ["lockerId", "referenceId"],
  },
  // --- Doors ---
  {
    label: "POST /mobilus/doors/open/{doorId}",
    method: "POST",
    endpoint: "/mobilus/doors/open/{doorId}",
    params: ["doorId"],
  },
  // --- Reservations ---
  {
    label: "GET /mobilus/reservations/{page}/{count}",
    method: "GET",
    endpoint: "/mobilus/reservations/{page}/{count}",
    params: ["page", "count"],
  },
  {
    label: "GET /mobilus/reservations/{reservationId}",
    method: "GET",
    endpoint: "/mobilus/reservations/{reservationId}",
    params: ["reservationId"],
  },
  {
    label: "PATCH /mobilus/reservations/{reservationId}/{status}",
    method: "PATCH",
    endpoint: "/mobilus/reservations/{reservationId}/{status}",
    params: ["reservationId", "status"],
  },
  {
    label: "PATCH /mobilus/reservations/expiration/{reservationId}/{expirationDate}",
    method: "PATCH",
    endpoint: "/mobilus/reservations/expiration/{reservationId}/{expirationDate}",
    params: ["reservationId", "expirationDate"],
  },
  {
    label: "PATCH /mobilus/reservations/amount/{reservationId}/{amount}",
    method: "PATCH",
    endpoint: "/mobilus/reservations/amount/{reservationId}/{amount}",
    params: ["reservationId", "amount"],
  },
  // --- Transactions ---
  {
    label: "GET /mobilus/reservations/transactions/reservation/{reservationId}",
    method: "GET",
    endpoint: "/mobilus/reservations/transactions/reservation/{reservationId}",
    params: ["reservationId"],
  },
  // --- SQS Configs ---
  {
    label: "GET /mobilus/sqs-configs/{page}/{count}",
    method: "GET",
    endpoint: "/mobilus/sqs-configs/{page}/{count}",
    params: ["page", "count"],
  },
  {
    label: "GET /mobilus/sqs-configs/{tag}",
    method: "GET",
    endpoint: "/mobilus/sqs-configs/{tag}",
    params: ["tag"],
  },
  {
    label: "PATCH /mobilus/sqs-configs/{tag}",
    method: "PATCH",
    endpoint: "/mobilus/sqs-configs/{tag}",
    params: ["tag"],
  },
  {
    label: "DELETE /mobilus/sqs-configs/{tag}",
    method: "DELETE",
    endpoint: "/mobilus/sqs-configs/{tag}",
    params: ["tag"],
  },
  {
    label: "GET /mobilus/sqs-configs/docs/schemas/event",
    method: "GET",
    endpoint: "/mobilus/sqs-configs/docs/schemas/event",
    params: [],
  },
]

export const SaSstv2ApiTester: FC = () => {
  const [selectedMethodIndex, setSelectedMethodIndex] = useState(0)
  const [paramValues, setParamValues] = useState<Record<string, string>>({
    page: "1",
    count: "10",
  })

  const [triggerApiTest, apiTestResult] = usePostSstv2ApiTestMutation()

  const selectedMethod = API_METHODS[selectedMethodIndex]

  const resolvedEndpoint = useMemo(() => {
    let ep = selectedMethod.endpoint
    for (const param of selectedMethod.params) {
      ep = ep.replace(`{${param}}`, paramValues[param] || "")
    }
    return ep
  }, [selectedMethod, paramValues])

  const handleParamChange = useCallback(
    (paramName: string, value: string) => {
      setParamValues((prev) => ({ ...prev, [paramName]: value }))
    },
    [],
  )

  const handleExecute = useCallback(() => {
    triggerApiTest({
      method: selectedMethod.method,
      endpoint: resolvedEndpoint,
    })
  }, [triggerApiTest, selectedMethod.method, resolvedEndpoint])

  return (
    <Stack data-ui="UI-SL2W-242" direction="column" spacing={3} width="100%">
      {/* Method Selector */}
      <Card variant="outlined">
        <CardHeader title="API Tester" />
        <CardContent>
          <Stack direction="column" spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>API Method</InputLabel>
              <Select
                value={selectedMethodIndex}
                label="API Method"
                onChange={(e) => {
                  setSelectedMethodIndex(e.target.value as number)
                }}
              >
                {API_METHODS.map((m, idx) => (
                  <MenuItem key={idx} value={idx}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={m.method}
                        size="small"
                        color={
                          m.method === "GET" ? "info"
                            : m.method === "DELETE" ? "error"
                            : "warning"
                        }
                        sx={{ fontFamily: "monospace", fontWeight: "bold" }}
                      />
                      <Typography variant="body2" fontFamily="monospace">
                        {m.endpoint}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Dynamic input fields */}
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {selectedMethod.params.map((param) => (
                <TextField
                  key={param}
                  label={param}
                  size="small"
                  value={paramValues[param] || ""}
                  onChange={(e) => handleParamChange(param, e.target.value)}
                  sx={{ minWidth: 140 }}
                  placeholder={param}
                />
              ))}
            </Stack>

            {/* Resolved endpoint preview */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="textSecondary">
                Resolved:
              </Typography>
              <Typography
                variant="caption"
                fontFamily="monospace"
                sx={{
                  bgcolor: "rgba(0, 0, 0, 0.06)",
                  px: 1,
                  borderRadius: 1,
                }}
              >
                {selectedMethod.method} {resolvedEndpoint}
              </Typography>
            </Stack>

            {/* Execute button */}
            <Button
              variant="contained"
              size="small"
              startIcon={
                apiTestResult.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrow />
                )
              }
              onClick={handleExecute}
              disabled={apiTestResult.isLoading}
              sx={{ alignSelf: "flex-start" }}
            >
              Execute
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Response Viewer */}
      {(apiTestResult.data || apiTestResult.error) && (
        <Card variant="outlined">
          <CardHeader
            title="Response"
            action={
              <Stack direction="row" spacing={1}>
                {apiTestResult.data?.data?.success != null && (
                  <Chip
                    label={apiTestResult.data.data.success ? "Success" : "Error"}
                    size="small"
                    color={apiTestResult.data.data.success ? "success" : "error"}
                    variant="outlined"
                  />
                )}
                {apiTestResult.data?.data?.latency != null && (
                  <Chip
                    label={`${apiTestResult.data.data.latency}ms`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            }
          />
          <CardContent>
            <Box
              sx={{
                maxHeight: 600,
                overflow: "auto",
                bgcolor: "rgba(0, 0, 0, 0.02)",
                borderRadius: 1,
                p: 1,
              }}
            >
              {apiTestResult.data?.data?.error && (
                <Typography variant="body2" color="error" gutterBottom>
                  {apiTestResult.data.data.error}
                </Typography>
              )}
              {apiTestResult.error ? (
                <JsonView data={apiTestResult.error as any} />
              ) : apiTestResult.data?.data?.response != null ? (
                <JsonView data={apiTestResult.data.data.response as any} />
              ) : !apiTestResult.data?.data?.error ? (
                <Typography variant="body2" color="textSecondary">
                  No response data
                </Typography>
              ) : null}
            </Box>
          </CardContent>
        </Card>
      )}
    </Stack>
  )
}

export default SaSstv2ApiTester
